/**
 * "Invite people": turns what the user typed or pasted (commas, spaces, new lines, "Name <a@b.c>") into a clean,
 * de-duplicated email list, and decides what the invite step can submit.
 */
import { isValidEmail } from "./auth.ts";

/** Same cap as CreateInvitesRequest.emails. */
export const MAX_INVITE_EMAILS = 50;

export type EmailList = {
  /** Lowercased, de-duplicated, in input order (at most MAX_INVITE_EMAILS). */
  valid: string[];
  /** Tokens that aren't email addresses, as typed. */
  invalid: string[];
  /** More than MAX_INVITE_EMAILS valid addresses were given (the rest are dropped). */
  overLimit: boolean;
};

function stripWrapping(token: string): string {
  let t = token.trim();
  if (t.toLowerCase().startsWith("mailto:")) t = t.slice(7);
  const lt = t.lastIndexOf("<");
  const gt = t.lastIndexOf(">");
  if (lt >= 0 && gt > lt) t = t.slice(lt + 1, gt);
  while (t.length > 0 && (t[0] === "<" || t[0] === "\"" || t[0] === "'" || t[0] === "(")) t = t.slice(1);
  while (t.length > 0 && ">\"').".includes(t[t.length - 1]!)) t = t.slice(0, -1);
  return t.trim();
}

/** A word without "@" directly followed (through more words) by "<address>" is part of "Name <address>". */
function isDisplayName(tokens: string[], i: number): boolean {
  for (let k = i + 1; k < tokens.length; k++) {
    const t = tokens[k]!.trim();
    if (t.startsWith("<")) return true;
    if (t.includes("@")) return false;
  }
  return false;
}

/** Splits on commas, semicolons and whitespace; "Ana Souza <ana@x.com>" keeps only the address. */
export function parseEmailList(text: string): EmailList {
  const valid: string[] = [];
  const invalid: string[] = [];
  let overLimit = false;
  let current = "";
  const tokens: string[] = [];
  let inAngle = false;
  for (const ch of text) {
    if (ch === "<") inAngle = true;
    if (ch === ">") inAngle = false;
    const sep = ch === "," || ch === ";" || ch === "\n" || ch === "\r" || ch === "\t" || (ch === " " && !inAngle);
    if (sep) {
      if (current.trim() !== "") tokens.push(current);
      current = "";
    } else current += ch;
  }
  if (current.trim() !== "") tokens.push(current);
  for (let i = 0; i < tokens.length; i++) {
    const raw = tokens[i]!;
    const email = stripWrapping(raw).toLowerCase();
    if (email === "") continue;
    if (!raw.includes("@") && isDisplayName(tokens, i)) continue;
    if (!isValidEmail(email)) {
      invalid.push(raw.trim());
      continue;
    }
    if (valid.includes(email)) continue;
    if (valid.length >= MAX_INVITE_EMAILS) {
      overLimit = true;
      continue;
    }
    valid.push(email);
  }
  return { valid, invalid, overLimit };
}
